import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Button } from 'react-bootstrap';
import { PageHeader, ContentMessage } from 'axway-react-ui';
import Icon from 'components/widgets/Icon';
import Breadcrumbs from 'components/widgets/Breadcrumbs';
import OASView from './OASView';
import FlowTriggerErrorsView from './FlowTriggerErrorsView';

export class SpecView extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);

		this.deleteSpec = this.deleteSpec.bind(this);
		this.deleteSpecCancel = this.deleteSpecCancel.bind(this);
		this.onCloseSpecDelete = this.onCloseSpecDelete.bind(this);

		this.state = {
			showDeleteModal: false
		};
	}

	renderSummary() {
		const { spec, specType, auth, triggerTypeId, triggeredFlows } = this.props;
		if (specType === 'openapi') {
			return (
				<OASView
					spec={spec}
					auth={auth}
					triggerTypeId={triggerTypeId}
					triggeredFlows={triggeredFlows}
				/>
			);
		}
		return null;
	}

	renderInvalidTriggers() {
		const { invalidTriggers } = this.props;
		const hasInvalidTriggers = Object.keys(invalidTriggers).length > 0;
		if (hasInvalidTriggers) {
			return (
				<FlowTriggerErrorsView invalidTriggers={invalidTriggers} />
			);
		}
		return null;
	}

	onSpecDelete() {
		this.setState({
			showDeleteModal: true
		});
	}

	deleteSpec() {
		this.setState({
			showDeleteModal: false
		}, () => {
			this.props.onDelete(this.props.triggerTypeId);
		});
	}

	deleteSpecCancel() {
		this.setState({
			showDeleteModal: false
		});
	}

	onCloseSpecDelete() {
		this.props.onDeleteComplete();
	}

	renderDeleteModal() {
		const {
			specTypeName
		} = this.props;

		return (
			<ContentMessage
				className="lg warning delete"
				title={this.intl.formatMsg('Are you sure you want to delete this {specTypeName} specification?', { specTypeName })}
				summary={this.intl.formatMsg('This process is irreversible and will delete all associated flows.')}
				actionLabel={this.intl.formatMsg('Proceed')}
				cancelLabel={this.intl.formatMsg('Cancel')}
				onAction={this.deleteSpec}
				onClose={this.deleteSpecCancel}
				imgClass="gg icon-GG-warning"
			/>
		);
	}

	render() {
		const {
			specType,
			specTypeName,
			specName,
			onUpdate,
			deleteSpecError,
			url
		} = this.props;

		const deleteButtonLabel = this.intl.formatMsg('Delete');
		const updateButtonLabel = this.intl.formatMsg('Update');
		return (
			<div className="spec-view">
				{this.state.showDeleteModal && this.renderDeleteModal()}
				{ deleteSpecError && (
					<ContentMessage
						confirm
						className="lg error delete"
						title={this.intl.formatMsg('Failed to delete {specTypeName} specification.', { specTypeName })}
						summary={this.intl.formatMsg('Please see the console for more information.')}
						actionLabel={this.intl.formatMsg('Close')}
						onAction={this.onCloseSpecDelete}
						imgClass="gg icon-GG-cloud-cross"
					/>
				)}
				<PageHeader>
					<PageHeader.Section align="left">
						<Breadcrumbs
							crumbs={[
								{
									path: `${window.$conf.prefix}/project/apidocs`,
									name: this.intl.formatMsg('API Doc & Test')
								},
								{ name: specName }
							]}
						/>
					</PageHeader.Section>
					<PageHeader.Section align="right">
						<Button
							bsSize="large"
							bsStyle="danger"
							className="deemphasize"
							onClick={() => this.onSpecDelete()}
						>
							<Icon className="delete-spec-icon" src="icon-GG-trash2" />
							{deleteButtonLabel}
						</Button>
						<Button
							bsSize="large"
							className="compact"
							onClick={() => onUpdate(specType)}
						>
							<Icon src="icon-GG-upload2" />
							{updateButtonLabel}
						</Button>
						{url && (
							<Button
								className="action-button"
								bsSize="large"
								type="button"
								href={url}
								download
							>
								<Icon src="icon-GG-download2" />
								{this.intl.formatMsg('Download')}
							</Button>
						)}
					</PageHeader.Section>
				</PageHeader>
				{this.renderInvalidTriggers()}
				{this.renderSummary()}
			</div>
		);
	}
}

SpecView.propTypes = {
	/**
	 * API spec definition (decoded)
	 */
	spec: PropTypes.object.isRequired,
	specType: PropTypes.string.isRequired,
	specTypeName: PropTypes.string.isRequired,
	specName: PropTypes.string.isRequired,
	url: PropTypes.string,
	/**
	 * The auth details for the project
	 */
	auth: PropTypes.shape({
		apikey: PropTypes.string,
		// apikey, none, basic, etc...
		apiPrefixSecurity: PropTypes.string.isRequired
	}).isRequired,
	/**
	 * The flow-trigger type related to this protocol view
	 */
	triggerTypeId: PropTypes.string.isRequired,
	/**
	 * A map of configured methods that triggered flows
	 */
	triggeredFlows: PropTypes.object.isRequired,
	invalidTriggers: PropTypes.object.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	deleteSpecError: PropTypes.bool.isRequired
};

export default injectIntl(SpecView);
